import { useMemo, useState } from 'react';
import {
  AnalyticsFilters,
  Granularity,
  UserAnalyticsResponse,
  MatterAnalyticsResponse,
  useAnalyticsOverview,
  useAllMattersForAnalytics,
} from '@/hooks/useAnalytics';
import useAllUsers from '@/hooks/useAllUsers';
import {
  ActiveUsersChart,
  AnalyticsFiltersBar,
  SummaryStats,
  ChartsSection,
  TopUsersMatters,
} from './components';

interface AnalyticsDashboardProps {
  dateFrom: Date;
  dateTo: Date;
  granularity: Granularity;
  unfilteredActiveUsersData: Array<{ date: string; value: number }> | undefined;
  usersData: UserAnalyticsResponse | undefined;
  usersLoading: boolean;
  mattersData: MatterAnalyticsResponse | undefined;
  mattersLoading: boolean;
}

export const AnalyticsDashboard = ({
  dateFrom,
  dateTo,
  granularity,
  unfilteredActiveUsersData,
  usersData,
  usersLoading,
  mattersData,
  mattersLoading,
}: AnalyticsDashboardProps) => {
  // Matter/User filter state (scoped to this component)
  const [matterGuid, setMatterGuid] = useState<string | null>(null);
  const [userGuid, setUserGuid] = useState<string | null>(null);

  // Build filters object for data fetching
  const filters: AnalyticsFilters = {
    dateFrom,
    dateTo,
    matterGuid,
    userGuid,
    granularity,
  };

  // Data fetching hooks
  const { data: analyticsData, isLoading: isLoadingAnalytics } = useAnalyticsOverview(filters);
  const { matters } = useAllMattersForAnalytics();
  const { users } = useAllUsers();
  const usersWithAccountStatus = useMemo(() => {
    const accountStatusByGuid = new Map(
      usersData?.users.map(user => [user.user_guid, user.is_active])
    );

    return users.map(user => ({
      ...user,
      is_active: accountStatusByGuid.get(user.guid) ?? true,
    }));
  }, [users, usersData?.users]);

  return (
    <div className="space-y-6">
      {/* Organization Overview - Unfiltered Active Users Chart */}
      <ActiveUsersChart
        unfilteredActiveUsersData={unfilteredActiveUsersData}
        dateFrom={dateFrom}
        dateTo={dateTo}
        granularity={granularity}
      />

      {/* Filtered Analytics Container */}
      <div className="space-y-6 rounded-xl border border-border bg-card p-6">
        {/* Matter & User Filters */}
        <AnalyticsFiltersBar
          matterGuid={matterGuid}
          userGuid={userGuid}
          matters={matters}
          users={usersWithAccountStatus}
          onMatterChange={setMatterGuid}
          onUserChange={setUserGuid}
        />

        {/* Summary Stats */}
        <SummaryStats analyticsData={analyticsData} isLoading={isLoadingAnalytics} />

        {/* Charts Section */}
        {analyticsData && (
          <ChartsSection
            analyticsData={analyticsData}
            dateFrom={dateFrom}
            dateTo={dateTo}
            granularity={granularity}
            filters={filters}
          />
        )}
      </div>

      {/* Top Users & Matters */}
      <TopUsersMatters
        usersData={usersData}
        usersLoading={usersLoading}
        mattersData={mattersData}
        mattersLoading={mattersLoading}
      />
    </div>
  );
};
